/**
 * useInvoicePageData - Compound Hook for Invoice Page Data Management
 *
 * This hook encapsulates all the interdependent data queries and hooks
 * required for invoice editing, eliminating the need for strict hook
 * ordering comments in components.
 */

import { useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import { trpc } from '@lib/trpc';
import { useStatuses } from '@hooks/useStatuses';
import { useInvoiceData } from '@hooks/useInvoiceData';
import { useInvoicePdf } from '@hooks/useInvoicePdf';
import { useInvoicePermissions } from '@hooks/useInvoicePermissions';
import { useInvoiceStatus } from '@hooks/useInvoiceStatus';
import toast from 'react-hot-toast';

export const useInvoicePageData = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Core status management
  const { getNextStatus, getStatusByKey, isLoading: isStatusLoading } = useStatuses();

  // tRPC utilities for cache management
  const utils = trpc.useUtils();

  // Primary data queries (always called in consistent order)
  const { data: invoice, isLoading: isLoadingInvoice } = trpc.invoice.getById.useQuery(
    { id: id! },
    { enabled: !!id }
  );

  const { data: storesData, isLoading: isLoadingStores } = trpc.store.getAll.useQuery();
  const { data: suppliersData, isLoading: isLoadingSuppliers } = trpc.supplier.getAll.useQuery();

  const { data: pdfData, isLoading: isLoadingPdf } = trpc.storage.getPdf.useQuery(
    { filename: invoice?.pdf_filename || '' },
    { enabled: !!invoice?.pdf_filename }
  );

  // Extracted form data management
  const {
    formData,
    setFormData,
    currentStatus,
    setCurrentStatus,
    selectedStore,
    setSelectedStore,
    selectedSupplier,
    setSelectedSupplier,
    buildInvoicePayload,
    handleInputChange,
  } = useInvoiceData({ invoice, storesData, suppliersData });

  // Conditional audit data fetching
  const needsAuditData = useMemo(() => {
    return currentStatus === 'adding' || currentStatus === 'ir' || currentStatus === 'invoicing';
  }, [currentStatus]);

  const { data: auditData } = trpc.audit.getByEntity.useQuery(
    {
      entityType: 'invoice',
      entityId: id!,
      limit: 10,
      offset: 0,
    },
    {
      enabled: !!id && !!invoice && needsAuditData,
      staleTime: 10 * 60 * 1000,
    }
  );

  // PDF processing
  const { pdfUrl } = useInvoicePdf({ pdfData });

  // Permission management
  const permissions = useInvoicePermissions({ currentStatus, invoice, auditData });

  // Status management
  const statusInfo = useInvoiceStatus({ currentStatus, formData });

  // Computed loading states
  const isLoading = isLoadingInvoice || isLoadingStores || isLoadingSuppliers || isStatusLoading;
  const isDataReady = !!invoice && !!storesData && !!suppliersData && !isStatusLoading;

  // Cache invalidation helper
  const invalidateInvoiceCache = useCallback(() => {
    utils.invoice.getInvoices.invalidate();
    utils.invoice.getInvoiceCounts.invalidate();
  }, [utils]);

  return {
    // Core data
    id,
    navigate,
    user,
    invoice,
    storesData,
    suppliersData,
    pdfData,
    pdfUrl,
    auditData,

    // Form management
    formData,
    setFormData,
    currentStatus,
    setCurrentStatus,
    selectedStore,
    setSelectedStore,
    selectedSupplier,
    setSelectedSupplier,
    buildInvoicePayload,
    handleInputChange,

    // Permissions (spread for easier access)
    ...permissions,

    // Status info (spread for easier access)
    ...statusInfo,

    // Loading states
    isLoading,
    isLoadingInvoice,
    isLoadingStores,
    isLoadingPdf,
    isDataReady,

    // Utilities
    utils,
    invalidateInvoiceCache,

    // Helper functions for common operations
    getStatusByKey,
    getNextStatus,
  };
};

/**
 * useInvoiceMutations - Separate hook for mutations to avoid re-renders
 */
export const useInvoiceMutations = (
  id: string | undefined,
  setCurrentStatus: (status: string) => void,
  utils: ReturnType<typeof trpc.useUtils>,
  invalidateInvoiceCache: () => void,
  navigate: (path: string) => void,
  returnUrl?: string
) => {
  const updateStatusMutation = trpc.invoice.updateStatus.useMutation({
    onSuccess: (_, variables) => {
      toast.success('Status updated successfully');
      setCurrentStatus(variables.status);
      utils.invoice.getById.invalidate({ id: id! });
      invalidateInvoiceCache();
    },
    onError: error => {
      toast.error(error.message || 'Failed to update status');
    },
  });

  const updateInvoiceMutation = trpc.invoice.update.useMutation({
    onSuccess: () => {
      toast.success('Invoice updated successfully');
      navigate(returnUrl || '/invoices');
      utils.invoice.getById.invalidate({ id: id! });
      invalidateInvoiceCache();
    },
    onError: error => {
      toast.error(error.message || 'Failed to update invoice');
    },
  });

  const assignToMeMutation = trpc.invoice.assignToMe.useMutation({
    onSuccess: () => {
      toast.success('Invoice assigned to you successfully');
      utils.invoice.getById.invalidate({ id: id! });
      invalidateInvoiceCache();
    },
    onError: error => {
      toast.error(error.message || 'Failed to assign invoice');
    },
  });

  const unassignMutation = trpc.invoice.unassign.useMutation({
    onSuccess: () => {
      toast.success('Invoice unassigned successfully');
      utils.invoice.getById.invalidate({ id: id! });
      invalidateInvoiceCache();
    },
    onError: error => {
      toast.error(error.message || 'Failed to unassign invoice');
    },
  });

  return {
    updateStatusMutation,
    updateInvoiceMutation,
    assignToMeMutation,
    unassignMutation,
  };
};
